import React, { useState, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  useToast,
  FormErrorMessage,
  HStack,
  Switch,
  Text,
  Divider,
} from "@chakra-ui/react";
import biometricDeviceService from "../../services/biometricDeviceService";

const DeviceDrawer = ({ isOpen, onClose, device, onSuccess }) => {
  const toast = useToast();
  const isEditMode = !!device;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    deviceName: "",
    deviceCode: "",
    ipAddress: "",
    port: 4370,
    serialNumber: "",
    vendor: "ZKTeco",
    location: "",
    timezone: "Asia/Kolkata",
    syncMode: "PULL",
    syncInterval: 30,
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState({});

  // Update form when device changes
  useEffect(() => {
    if (device) {
      setFormData({
        deviceName: device.deviceName || "",
        deviceCode: device.deviceCode || "",
        ipAddress: device.ipAddress || "",
        port: device.port || 4370,
        serialNumber: device.serialNumber || "",
        vendor: device.vendor || "ZKTeco",
        location: device.location || "",
        timezone: device.timezone || "Asia/Kolkata",
        syncMode: device.syncMode || "PULL",
        syncInterval: device.syncInterval || 30,
        status: device.status || "ACTIVE",
      });
    } else {
      // Reset form for add mode
      setFormData({
        deviceName: "",
        deviceCode: "",
        ipAddress: "",
        port: 4370,
        serialNumber: "",
        vendor: "ZKTeco",
        location: "",
        timezone: "Asia/Kolkata",
        syncMode: "PULL",
        syncInterval: 30,
        status: "ACTIVE",
      });
    }
    setErrors({});
  }, [device, isOpen]);

  // Create/Update handler
  const handleSubmitForm = async (data) => {
    try {
      setIsSubmitting(true);
      const response = isEditMode
        ? await biometricDeviceService.updateDevice(device._id, data)
        : await biometricDeviceService.createDevice(data);

      toast({
        title: isEditMode ? "Device updated" : "Device created",
        description: response.message || "Operation successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onSuccess();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      const validationErrors = error.response?.data?.errors || {};

      toast({
        title: isEditMode ? "Error updating device" : "Error creating device",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      // Set field-level errors
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.deviceName.trim()) {
      newErrors.deviceName = "Device name is required";
    }

    if (!formData.deviceCode.trim()) {
      newErrors.deviceCode = "Device code is required";
    }

    if (!formData.ipAddress.trim()) {
      newErrors.ipAddress = "IP address is required";
    } else {
      // Basic IP validation
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!ipRegex.test(formData.ipAddress)) {
        newErrors.ipAddress = "Invalid IP address format";
      }
    }

    if (!formData.port || formData.port < 1 || formData.port > 65535) {
      newErrors.port = "Port must be between 1 and 65535";
    }

    if (!formData.vendor) {
      newErrors.vendor = "Vendor is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    handleSubmitForm(formData);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          {isEditMode ? "Edit Device" : "Add New Device"}
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing={4} align="stretch">
            {/* Device Name */}
            <FormControl isRequired isInvalid={!!errors.deviceName}>
              <FormLabel>Device Name</FormLabel>
              <Input
                value={formData.deviceName}
                onChange={(e) => handleChange("deviceName", e.target.value)}
                placeholder="e.g., Main Office Entrance"
              />
              <FormErrorMessage>{errors.deviceName}</FormErrorMessage>
            </FormControl>

            {/* Device Code */}
            <FormControl
              isRequired
              isInvalid={!!errors.deviceCode}
              isDisabled={isEditMode}
            >
              <FormLabel>Device Code</FormLabel>
              <Input
                value={formData.deviceCode}
                onChange={(e) => handleChange("deviceCode", e.target.value)}
                placeholder="e.g., DEV001"
                isReadOnly={isEditMode}
              />
              <FormErrorMessage>{errors.deviceCode}</FormErrorMessage>
              {isEditMode && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Device code cannot be changed
                </Text>
              )}
            </FormControl>

            <Divider />

            {/* IP Address */}
            <FormControl isRequired isInvalid={!!errors.ipAddress}>
              <FormLabel>IP Address</FormLabel>
              <Input
                value={formData.ipAddress}
                onChange={(e) => handleChange("ipAddress", e.target.value)}
                placeholder="192.168.1.100"
              />
              <FormErrorMessage>{errors.ipAddress}</FormErrorMessage>
            </FormControl>

            {/* Port */}
            <FormControl isRequired isInvalid={!!errors.port}>
              <FormLabel>Port</FormLabel>
              <NumberInput
                value={formData.port}
                onChange={(valueString, valueNumber) =>
                  handleChange("port", valueNumber)
                }
                min={1}
                max={65535}
              >
                <NumberInputField placeholder="4370" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormErrorMessage>{errors.port}</FormErrorMessage>
            </FormControl>

            {/* Serial Number */}
            <FormControl>
              <FormLabel>Serial Number</FormLabel>
              <Input
                value={formData.serialNumber}
                onChange={(e) => handleChange("serialNumber", e.target.value)}
                placeholder="Optional"
              />
            </FormControl>

            <Divider />

            {/* Vendor */}
            <FormControl isRequired isInvalid={!!errors.vendor}>
              <FormLabel>Vendor</FormLabel>
              <Select
                value={formData.vendor}
                onChange={(e) => handleChange("vendor", e.target.value)}
              >
                {biometricDeviceService.getVendors().map((vendor) => (
                  <option key={vendor.value} value={vendor.value}>
                    {vendor.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.vendor}</FormErrorMessage>
            </FormControl>

            {/* Location */}
            <FormControl>
              <FormLabel>Location</FormLabel>
              <Input
                value={formData.location}
                onChange={(e) => handleChange("location", e.target.value)}
                placeholder="e.g., Head Office, Mumbai"
              />
            </FormControl>

            {/* Timezone */}
            <FormControl isRequired>
              <FormLabel>Timezone</FormLabel>
              <Select
                value={formData.timezone}
                onChange={(e) => handleChange("timezone", e.target.value)}
              >
                {biometricDeviceService.getTimezones().map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Sync Mode */}
            <FormControl isRequired>
              <FormLabel>Sync Mode</FormLabel>
              <Select
                value={formData.syncMode}
                onChange={(e) => handleChange("syncMode", e.target.value)}
              >
                {biometricDeviceService.getSyncModes().map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {formData.syncMode === "PULL"
                  ? "System will fetch logs from device periodically"
                  : "Device will push logs to system"}
              </Text>
            </FormControl>

            {/* Sync Interval (only for PULL mode) */}
            {formData.syncMode === "PULL" && (
              <FormControl isRequired>
                <FormLabel>Sync Interval (minutes)</FormLabel>
                <NumberInput
                  value={formData.syncInterval}
                  onChange={(valueString, valueNumber) =>
                    handleChange("syncInterval", valueNumber)
                  }
                  min={5}
                  max={1440}
                  step={5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  How often to fetch logs (5 to 1440 minutes)
                </Text>
              </FormControl>
            )}

            {/* Status */}
            <FormControl isRequired>
              <FormLabel>Status</FormLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
              >
                {biometricDeviceService.getDeviceStatuses().map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button
            variant="outline"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
          >
            {isEditMode ? "Update Device" : "Create Device"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default DeviceDrawer;
